import React, { useEffect, useMemo, useState } from 'react';
import { useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import { listLppForYear, upsertLpp } from '../../api/hr';
import { formatNumber } from '../../utils/format';
import EditableCell from './EditableCell';
import PersonTh from '../../components/PersonTh';
import PageHeader from '../../components/PageHeader';
import CardShell from '../../components/CardShell';
import SegmentedToggle from '../../components/SegmentedToggle';
import SearchField from '../../components/SearchField';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import PersonName from '../../components/PersonName';
import { useTableSort } from '../../hooks/useTableSort';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { normalizeName, buildSearchKey } from '../../utils/personTable';
import './HrPage.css';

/* LPP — taux par personne et par année.
   Modèle rate-driven :
     • taux moyen par défaut du tenant (obligatoire) = plancher anti-trou ;
     • affinage par personne : on saisit un % OU un CHF mensuel (selon plan/âge
       du contrat en vigueur) → le % calculé est la source de vérité ;
     • ce taux se reporte aux années futures jusqu'à nouvelle saisie.
   Réf. = masse salariale prorata temporis des contrats de l'année (lecture seule). */

const SRC_LABEL = {
  explicit: 'saisi',
  carried:  'Report',
  borrowed: 'source',     // taux futur appliqué à une année passée
  exempt:   'exonéré',    // personne non assujettie (coche)
  default:  'défaut',     // aucun taux saisi → moyenne/défaut appliqué (signalé en rouge)
  none:     '—',
};

const pctFmt = (v) => (v == null || v === '' ? '—' : `${formatNumber(Number(v) * 100, 2)}%`);
const pctParse = (s) => (s === '' || s == null ? null : Number(s) / 100);

export default function LppPage({ activeTenant, setPage, year, embedded }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const [rows, setRows] = useState([]);
  const [totE, setTotE] = useState(0);
  const [totL, setTotL] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = usePersistentValue(`lpp_status_v2_${tenantId}`, 'all');  // 'active' | 'all'
  const sort = useTableSort(`lpp_sort_${tenantId}`, 'last_name_sort');
  const { sortCol, sortDir, handleSort, sortRows, nameOrder } = sort;
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [massOpen, setMassOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  function refresh() {
    if (!tenantId || !year) return;
    setLoading(true);
    setSelected(new Set());
    listLppForYear(tenantId, year, status)
      .then(r => {
        if (r.error) { setError(r.error); return; }
        const d = r.data;
        setRows(d.rows || []);
        setTotE(Number(d.total_empr_chf_monthly || 0));
        setTotL(Number(d.total_empl_chf_monthly || 0));
      })
      .finally(() => setLoading(false));
  }
  useEffect(refresh, [tenantId, year, status]); // eslint-disable-line

  // Champs dérivés pour tri + recherche (mêmes clés que les autres tables de personnes).
  const enrichedRows = useMemo(() => rows.map(r => ({
    ...r,
    first_name_sort: (r.first_name || '').toLowerCase(),
    last_name_sort:  (r.last_name  || '').toLowerCase(),
    is_exempt_sort:  r.is_exempt ? 1 : 0,
    empr_annual: Number(r.lpp_empr_chf_monthly || 0) * 12,
    empl_annual: Number(r.lpp_empl_chf_monthly || 0) * 12,
    _search: buildSearchKey(r.first_name, r.last_name, r.department),
  })), [rows]);

  const filteredRows = useMemo(() => {
    const q = normalizeName(search.trim());
    if (!q) return enrichedRows;
    return enrichedRows.filter(r => r._search.includes(q));
  }, [enrichedRows, search]);

  const sortedRows = useMemo(
    () => sortRows(filteredRows),
    [filteredRows, sortCol, sortDir], // eslint-disable-line
  );

  // Synthèse : taux moyen LPP RÉELLEMENT appliqué, pondéré par le salaire de
  // référence, sur TOUTES les lignes de la vue (saisi / reporté / source / défaut).
  // Taux moyen = Σ CHF LPP annuel ÷ Σ salaire de référence.
  const avgRates = useMemo(() => {
    let refSum = 0, chfE = 0, chfL = 0;
    for (const r of filteredRows) {
      if (r.is_exempt || r.source === 'exempt') continue;   // exonérés hors moyenne
      const ref = Number(r.reference_salary_annual || 0);
      if (ref <= 0) continue;
      refSum += ref;
      chfE += Number(r.lpp_empr_chf_monthly || 0) * 12;
      chfL += Number(r.lpp_empl_chf_monthly || 0) * 12;
    }
    return refSum > 0 ? { empr: chfE / refSum, empl: chfL / refSum, refSum } : null;
  }, [filteredRows]);

  // Upsert d'une personne : on fige les DEUX taux/CHF (édité + résolu courant)
  // pour créer une ligne explicite de l'année.
  async function commitPct(row, which, dec) {
    // Saisir un taux lève l'exonération (is_exempt:false).
    const payload = {
      person_id: row.person_id, year, mode: 'pct', is_exempt: false,
      lpp_empr_pct: which === 'empr' ? dec : (row.lpp_empr_pct != null ? Number(row.lpp_empr_pct) : null),
      lpp_empl_pct: which === 'empl' ? dec : (row.lpp_empl_pct != null ? Number(row.lpp_empl_pct) : null),
    };
    const r = await upsertLpp(tenantId, payload);
    if (r.error) { setError(r.error); return; }
    applyRow(r.data);
  }
  async function commitChf(row, which, chf) {
    const payload = {
      person_id: row.person_id, year, mode: 'chf', is_exempt: false,
      lpp_empr_chf_monthly: which === 'empr' ? chf : Number(row.lpp_empr_chf_monthly || 0),
      lpp_empl_chf_monthly: which === 'empl' ? chf : Number(row.lpp_empl_chf_monthly || 0),
    };
    const r = await upsertLpp(tenantId, payload);
    if (r.error) { setError(r.error); return; }
    applyRow(r.data);
  }
  // Coche « exonéré » : aucune cotisation pour (personne, année).
  async function toggleExempt(row, checked) {
    const r = await upsertLpp(tenantId, { person_id: row.person_id, year, is_exempt: checked });
    if (r.error) { setError(r.error); return; }
    applyRow(r.data);
  }
  // Source éditable à la main : un clic bascule entre « saisie {year} » (fige le
  // taux résolu comme saisie de l'exercice) et report automatique (relâche).
  async function pinToggle(row) {
    if (row.source === 'exempt') return;            // exonéré : géré par la coche
    if (row.source === 'explicit') {
      // Relâcher : vider la ligne de l'année → retombe sur report / défaut.
      const r = await upsertLpp(tenantId, {
        person_id: row.person_id, year, mode: 'pct', is_exempt: false,
        lpp_empr_pct: null, lpp_empl_pct: null,
      });
      if (r.error) { setError(r.error); return; }
      applyRow(r.data);
      return;
    }
    // Figer le taux résolu (report / emprunté / défaut) comme saisie de l'exercice.
    const r = await upsertLpp(tenantId, {
      person_id: row.person_id, year, mode: 'pct', is_exempt: false,
      lpp_empr_pct: row.lpp_empr_pct != null ? Number(row.lpp_empr_pct) : null,
      lpp_empl_pct: row.lpp_empl_pct != null ? Number(row.lpp_empl_pct) : null,
    });
    if (r.error) { setError(r.error); return; }
    applyRow(r.data);
  }

  // ── Sélection de lignes (pour l'édition groupée) ───────────────────────────
  const allSelected = filteredRows.length > 0 && filteredRows.every(r => selected.has(r.person_id));
  const someSelected = selected.size > 0 && !allSelected;
  function toggleRow(pid) {
    setSelected(prev => { const n = new Set(prev); n.has(pid) ? n.delete(pid) : n.add(pid); return n; });
  }
  function toggleAll() {
    setSelected(prev => prev.size === filteredRows.length ? new Set() : new Set(filteredRows.map(r => r.person_id)));
  }
  function clearSelection() { setSelected(new Set()); }

  // Applique un taux (ou une exonération) aux lignes SÉLECTIONNÉES via upsert.
  async function applyMassEdit({ empr, empl, exempt }) {
    const ids = [...selected];
    if (ids.length === 0) return false;
    for (const pid of ids) {
      const row = rows.find(r => r.person_id === pid);
      if (!row) continue;
      const payload = exempt
        ? { person_id: pid, year, is_exempt: true }
        : {
            person_id: pid, year, mode: 'pct', is_exempt: false,
            lpp_empr_pct: empr != null ? empr : (row.lpp_empr_pct != null ? Number(row.lpp_empr_pct) : null),
            lpp_empl_pct: empl != null ? empl : (row.lpp_empl_pct != null ? Number(row.lpp_empl_pct) : null),
          };
      const r = await upsertLpp(tenantId, payload);
      if (r.error) { setError(r.error); return false; }
    }
    setMassOpen(false);
    clearSelection();
    refresh();
    return true;
  }
  function applyRow(updated) {
    setRows(prev => {
      const next = prev.map(r => r.person_id === updated.person_id ? updated : r);
      setTotE(next.reduce((a, r) => a + Number(r.lpp_empr_chf_monthly || 0), 0));
      setTotL(next.reduce((a, r) => a + Number(r.lpp_empl_chf_monthly || 0), 0));
      return next;
    });
  }

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></main>;

  // Colonnes triables — clés = champs (enrichis) des lignes.
  const columns = [
    { key: 'person',                label: p('Collaborateur'),        align: 'left', personSort: true },
    { key: 'department',            label: t('phrases:p_3d7c87c2af'), align: 'left' },
    { key: 'is_exempt_sort',        label: p('Exonéré'),              align: 'center', title: t('phrases:p_12bbf60ea3') },
    { key: 'source',                label: 'Source',                  align: 'left' },
    { key: 'reference_salary_annual', label: p('Réf. salaire (an)'),  align: 'right', title: t('phrases:p_111b8f1ea8') },
    { key: 'lpp_empr_pct',          label: '% empr',                  align: 'right' },
    { key: 'lpp_empl_pct',          label: '% empl',                  align: 'right' },
    { key: 'lpp_empr_chf_monthly',  label: p('LPP empr (CHF/mois)'),  align: 'right' },
    { key: 'lpp_empl_chf_monthly',  label: p('LPP empl (CHF/mois)'),  align: 'right' },
    { key: 'empr_annual',           label: p('LPP empr (CHF/an)'),    align: 'right', title: p('Cotisation annuelle = mensuel × 12 (lecture seule)') },
    { key: 'empl_annual',           label: p('LPP empl (CHF/an)'),    align: 'right', title: p('Cotisation annuelle = mensuel × 12 (lecture seule)') },
  ];

  /* Standard de carte : statut et recherche RÉDUISENT les lignes → toolbar ;
     l'édition groupée MODIFIE → action de l'entête. */
  const activeFilters = (status === 'active' ? 1 : 0) + (search.trim() ? 1 : 0);
  const filters = (
    <>
      <FilterIcon />
      <SegmentedToggle
        compact
        label={p('Statut')} value={status} onChange={setStatus}
        options={[
          { value: 'all', label: p('Tous') },
          { value: 'active', label: p('Actifs'), tone: 'alert' },
        ]}
      />
      <FilterCount count={activeFilters}
                   onClear={() => { setStatus('all'); setSearch(''); }} />
    </>
  );

  return (
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title={p('Cotisations LPP')}
          actions={setPage && <button className="hr-page__back" onClick={() => setPage('home')}>{p('← Accueil')}</button>}
        />
      )}

      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {!loading && (
        <CardShell
          title={<>LPP {year} <small>· {filteredRows.length} {filteredRows.length > 1 ? p('collaborateurs') : p('collaborateur')}</small></>}
          subtitle={avgRates
            ? `Taux moyen pondéré par le salaire de référence ; les personnes sans taux ni exonération le reçoivent.`
            : null}
          primaryAction={(
            <button className="lk-btn" onClick={() => setMassOpen(true)} disabled={selected.size === 0}>
              Édition groupée{selected.size > 0 ? ` (${selected.size})` : ''}
            </button>
          )}
          filters={filters}
          search={<SearchField value={search} onChange={setSearch}
                               placeholder={t('phrases:p_224c3d8b39')} />}
          filterCount={activeFilters}
          collapsible storageKey="lpp"
          /* Bande de synthèse (zone 7) : trois taux, une ligne — au lieu d'une
             card entière au-dessus du tableau (2026-08-27). */
          summary={avgRates ? (
            <>
              <div>
                <div className="lk-cs__kpi-k">Employeur</div>
                <div className="lk-cs__kpi-v">{pctFmt(avgRates.empr)}</div>
              </div>
              <div>
                <div className="lk-cs__kpi-k">{p('Employé')}</div>
                <div className="lk-cs__kpi-v">{pctFmt(avgRates.empl)}</div>
              </div>
              <div>
                <div className="lk-cs__kpi-k">{p('Total')}</div>
                <div className="lk-cs__kpi-v">{pctFmt(avgRates.empr + avgRates.empl)}</div>
              </div>
            </>
          ) : null}
          flush
        >
          <div className="hr-page__table-wrap">
            <table className="hr-page__table">
              <thead>
                <tr>
                  <th className="hr-page__check-col">
                    <input type="checkbox" checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll} />
                  </th>
                  {columns.map(col => {
                    if (col.personSort) {
                      return <PersonTh key={col.key} sort={sort} className="hr-page__sort-th" style={{ textAlign: col.align }} />;
                    }
                    const isSorted = sortCol === col.key;
                    return (
                      <th key={col.key}
                        className={`hr-page__sort-th${isSorted ? ' is-sorted' : ''}`}
                        style={{ textAlign: col.align }}
                        onClick={() => handleSort(col.key)}
                        title={col.title || p('Cliquer pour trier')}
                      >
                        <span>{col.label}</span>
                        <span className="hr-page__sort-ind">{isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => {
                  const srcLabel = r.source === 'explicit' && r.source_year ? t('phrases:p_9537cefc35', { year: r.source_year })
                    : r.source === 'carried' && r.source_year ? t('phrases:p_1801ea6ef3', { year: r.source_year })
                    : r.source === 'borrowed' && r.source_year ? t('phrases:p_7168557472', { year: r.source_year })
                    : p(SRC_LABEL[r.source] || r.source);
                  const srcEditable = r.source !== 'exempt';
                  const isSel = selected.has(r.person_id);
                  return (
                  <tr key={r.person_id} className={`${r.source === 'default' ? 'lpp-row--needs-rate' : ''}${isSel ? ' is-selected' : ''}`}>
                    <td className="hr-page__check-col">
                      <input type="checkbox" checked={isSel} onChange={() => toggleRow(r.person_id)} />
                    </td>
                    <td><PersonName firstName={r.first_name} lastName={r.last_name} order={nameOrder} /></td>
                    <td className={r.department ? '' : 'hr-page__muted'}>{r.department || '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={!!r.is_exempt}
                        onChange={e => toggleExempt(r, e.target.checked)}
                        title={t('phrases:p_06f6b16cdd')} />
                    </td>
                    <td>
                      {srcEditable ? (
                        <button type="button" className={`lpp-src lpp-src--${r.source} lpp-src--btn`}
                          onClick={() => pinToggle(r)}
                          title={r.source === 'explicit'
                            ? p('Cliquer pour relâcher → revenir au report automatique')
                            : t('phrases:p_13c07fc9f7', { year })}>
                          {srcLabel}
                        </button>
                      ) : (
                        <span className={`lpp-src lpp-src--${r.source}`}>{srcLabel}</span>
                      )}
                    </td>
                    <td className="hr-page__num">{formatNumber(Number(r.reference_salary_annual || 0), 0)}</td>
                    <td className="hr-page__num">
                      <EditableCell value={r.lpp_empr_pct} type="number" step={0.01} align="right"
                        formatter={pctFmt} parser={pctParse} onCommit={v => commitPct(r, 'empr', v)} />
                    </td>
                    <td className="hr-page__num">
                      <EditableCell value={r.lpp_empl_pct} type="number" step={0.01} align="right"
                        formatter={pctFmt} parser={pctParse} onCommit={v => commitPct(r, 'empl', v)} />
                    </td>
                    <td className="hr-page__num">
                      <EditableCell value={r.lpp_empr_chf_monthly} type="number" step={1} align="right"
                        formatter={v => formatNumber(Number(v || 0), 0)} onCommit={v => commitChf(r, 'empr', v ?? 0)} />
                    </td>
                    <td className="hr-page__num">
                      <EditableCell value={r.lpp_empl_chf_monthly} type="number" step={1} align="right"
                        formatter={v => formatNumber(Number(v || 0), 0)} onCommit={v => commitChf(r, 'empl', v ?? 0)} />
                    </td>
                    <td className="hr-page__num">{formatNumber(Number(r.lpp_empr_chf_monthly || 0) * 12, 0)}</td>
                    <td className="hr-page__num">{formatNumber(Number(r.lpp_empl_chf_monthly || 0) * 12, 0)}</td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="hr-page__total-row">
                  <td className="hr-page__check-col"></td>
                  <td><strong>{p('Total')}</strong></td>
                  <td></td><td></td><td></td><td></td><td></td><td></td>
                  <td className="hr-page__num"><strong>{formatNumber(totE, 0)}</strong></td>
                  <td className="hr-page__num"><strong>{formatNumber(totL, 0)}</strong></td>
                  <td className="hr-page__num"><strong>{formatNumber(totE * 12, 0)}</strong></td>
                  <td className="hr-page__num"><strong>{formatNumber(totL * 12, 0)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardShell>
      )}

      {error && (
        <div className="hr-modal__error" style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
          {error} <button onClick={() => setError(null)} style={{ marginLeft: 8 }}>×</button>
        </div>
      )}

      {massOpen && (
        <MassEditLppModal
          year={year}
          count={selected.size}
          onCancel={() => setMassOpen(false)}
          onApply={applyMassEdit}
        />
      )}
    </main>
  );
}


function MassEditLppModal({ year, count, onCancel, onApply }) {
  const { t } = useTranslation();
  const p = useP();
  const [empr, setEmpr] = useState('');
  const [empl, setEmpl] = useState('');
  const [exempt, setExempt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  async function apply() {
    if (!exempt && empr === '' && empl === '') { setErr(p('Renseignez au moins un taux, ou cochez « exonérer ».')); return; }
    setSubmitting(true); setErr(null);
    const ok = await onApply({
      empr: empr === '' ? null : Number(empr) / 100,
      empl: empl === '' ? null : Number(empl) / 100,
      exempt,
    });
    if (!ok) setSubmitting(false);
  }

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">Édition groupée LPP — {count} ligne{count > 1 ? 's' : ''} · {year}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}>
            <input type="checkbox" checked={exempt} onChange={e => setExempt(e.target.checked)} />
            {p('Exonérer la sélection (cotisation nulle)')}
          </label>
          <div className="hr-modal__field">
            <label className="hr-modal__label">Taux employeur (%)</label>
            <input className="hr-modal__input" type="number" step="0.01" value={empr} disabled={exempt}
              onChange={e => setEmpr(e.target.value)} placeholder="ex: 8.5" />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Taux employé (%)')}</label>
            <input className="hr-modal__input" type="number" step="0.01" value={empl} disabled={exempt}
              onChange={e => setEmpl(e.target.value)} placeholder="ex: 6.5" />
          </div>
          <div className="hr-modal__hint" style={{ fontSize: 12 }}>
            {t('phrases:p_7e825dc65a', { count, year })}
            {' '}{p('Un taux laissé vide conserve la valeur en vigueur de la personne. Le CHF mensuel est recalculé sur le salaire de référence.')}
          </div>
          {err && <div className="hr-modal__error">{err}</div>}
        </div>
        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel} disabled={submitting}>{t('common:actions.cancel')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary" onClick={apply} disabled={submitting}>
            {submitting ? p('Application…') : t('phrases:p_ac637d1baf', { count })}
          </button>
        </div>
      </div>
    </div>
  );
}
